import { useEffect } from "react";
import { Link, Navigate, useLocation, useParams } from "react-router-dom";
import SEO from "../components/SEO";
import { Eyebrow } from "../components/Reveal";

const LAST_UPDATED = "17 September 2026";

const POLICY_PAGES = {
  "privacy-policy": {
    title: "Privacy Policy",
    description: "How RK Group collects, uses and protects information submitted through this website.",
    body: [
      "RK Group respects the privacy of visitors using this website. This Privacy Policy explains, at a general level, how information submitted through our website may be collected, used and protected.",
      "Information may be provided by visitors when making an enquiry, requesting a catalogue, booking an appointment or site visit, submitting a business or landowner partnership enquiry, contacting our jewellery or real estate teams, or otherwise communicating with us through the website. Depending upon the interaction, information may include a visitor's name, telephone number, email address and other information voluntarily submitted through a form.",
      "Such information may be used to respond to enquiries, provide requested information, communicate regarding our products or services, arrange appointments or site visits, process business enquiries, improve our website experience, maintain appropriate business records and comply with applicable legal requirements. We do not intend to collect personal information that is unnecessary for the purpose for which it is requested.",
      "The website may use cookies and similar technologies for essential functionality, security, analytics and improvement of the website experience. Please refer to our Cookie Policy for further information.",
      "The website may also use or link to third-party services including Google Maps or other service providers. Their handling of information is governed by their respective terms and privacy practices.",
      "We take reasonable measures intended to protect information submitted through the website. However, no internet-based transmission or storage system can be guaranteed to be completely secure.",
      "This Privacy Policy may be updated from time to time to reflect changes in our website, business practices or applicable requirements.",
    ],
  },
  "terms-of-use": {
    title: "Terms of Use",
    description: "The terms governing use of the RK Group website and its content.",
    body: [
      "By accessing and using the RK Group website, you agree to use the website and its content for lawful informational purposes.",
      "The website presents information relating to RK Group and its businesses, including Ramesh Kapoor & Sons Jewellers Pvt. Ltd. and RK Builders. We endeavour to maintain accurate and current information. However, website content may be updated, revised or corrected where appropriate. Visitors should independently confirm material information directly with the relevant RK Group business before making a purchase, investment, booking or other decision.",
      "Images, text, graphics, logos, branding, layouts and other materials appearing on this website are protected by applicable intellectual-property rights and may not be reproduced, distributed or commercially used without appropriate authorisation.",
      "Links to third-party websites or services may be provided for convenience. RK Group does not control the content, availability or privacy practices of independent third-party services.",
      "Jewellery purchases, real estate transactions and other commercial arrangements may be subject to separate invoices, agreements, booking documents, terms, statutory requirements and other documentation, as applicable.",
      "These Terms may be revised periodically. Continued use of the website following an update constitutes use subject to the then-current Terms.",
    ],
  },
  "cookie-policy": {
    title: "Cookie Policy",
    description: "How the RK Group website uses cookies and similar technologies.",
    body: [
      "The RK Group website may use cookies and similar technologies to operate the website, maintain security, understand website performance and improve the visitor experience.",
      "Cookies are small data files that may be stored on a visitor's device when accessing a website. Depending on the technologies actually enabled on the website, cookies may include essential cookies required for website operation, preference cookies, analytics cookies and other technologies associated with integrated third-party services.",
      "Where consent is required for non-essential cookies, visitors should be provided with appropriate choices through the website's cookie controls. Visitors may also control certain cookies through their browser settings. Disabling some cookies may affect website functionality.",
      "The website's cookie-management implementation should accurately reflect the cookies and third-party technologies actually deployed on the live website.",
    ],
  },
};

const DISCLAIMER_SECTIONS = [
  {
    id: "gold-rate",
    num: "01",
    title: "Gold Rate Disclaimer",
    body: [
      "Gold, silver and other precious-metal rates displayed on this website are provided for general informational purposes. Precious-metal prices are dynamic and may change during the course of the day due to market conditions and other relevant factors.",
      "The rates displayed on the website should not be interpreted as a final quotation, invoice value or guaranteed transaction price. Final jewellery pricing may vary depending upon factors including purity, net metal weight, making charges, wastage where applicable, design, diamonds or other gemstones, applicable taxes and other relevant components.",
      "Customers should confirm the applicable rate and final price with Ramesh Kapoor & Sons Jewellers Pvt. Ltd. before completing a transaction.",
      "The live-rate module clearly displays the applicable purity, unit and the time at which the displayed rate was last updated, drawing from the configured legitimate data source.",
    ],
  },
  {
    id: "jewellery-pricing",
    num: "02",
    title: "Jewellery Pricing Disclaimer",
    body: [
      "Jewellery images, descriptions and information displayed on this website are intended to provide a general representation of our products and collections.",
      "Final prices may vary according to prevailing precious-metal rates, purity, product weight, making charges, diamond or gemstone specifications, design characteristics, applicable taxes and other relevant pricing components. Product appearance may also vary slightly due to photography, lighting, screen settings, craftsmanship and individual product characteristics.",
      "Display of a jewellery design on the website does not by itself guarantee current stock availability. For current availability, specifications and final pricing, customers are requested to contact Ramesh Kapoor & Sons Jewellers Pvt. Ltd. directly.",
    ],
  },
  {
    id: "real-estate",
    num: "03",
    title: "Real Estate Disclaimer",
    body: [
      "Information relating to RK Builders and its developments on this website is intended to provide general information about the business and its development portfolio.",
      "Project-specific information, where displayed, should be read together with the applicable approvals, sanctioned plans, specifications, agreements, statutory disclosures and other relevant documentation. Illustrations, architectural representations, plans, maps, photographs and other visual material should be identified appropriately where they are conceptual or representational rather than photographs of completed work.",
      "Prospective purchasers should verify all material project information, approvals, dimensions, specifications, availability, pricing and applicable statutory information before making a booking or transaction. Nothing on this website should be interpreted as replacing the definitive contractual or statutory documentation applicable to a particular transaction.",
    ],
  },
  {
    id: "rera",
    num: "04",
    title: "RERA Information",
    body: [
      "RK Builders is committed to presenting applicable project information in accordance with relevant legal and regulatory requirements.",
      "Where a project is required to be registered under the Real Estate (Regulation and Development) Act, 2016 and applicable rules, relevant RERA information should be displayed in the manner required by the applicable authority. Visitors considering a specific development are encouraged to review the applicable statutory information and official records before making a purchase decision.",
      "No RERA registration number, approval status or exemption is generated or assumed by this website. Such information is published only after it has been verified for the specific project, and is maintained individually for each relevant development.",
    ],
  },
  {
    id: "website",
    num: "05",
    title: "Website Disclaimer",
    body: [
      "The information contained on this website is provided for general information about RK Group and its businesses. While reasonable efforts are made to maintain accurate information, visitors should verify material information directly with the relevant RK Group business before relying upon it for a transaction or decision.",
      "Website content, including descriptions, images and other materials, may be updated or corrected as appropriate. Third-party links and services are provided for convenience where applicable. RK Group is not responsible for the independent content or practices of third-party websites.",
      "Nothing contained on this website should be construed as professional legal, financial or investment advice.",
    ],
  },
];

const RETIRED = {
  "gold-rate-disclaimer": "gold-rate",
  "jewellery-pricing-disclaimer": "jewellery-pricing",
  "real-estate-disclaimer": "real-estate",
  "rera-information": "rera",
  "website-disclaimer": "website",
};

const LegalShell = ({ title, subtitle, children, page, description }) => (
  <div data-testid={`legal-${page}`} className="pt-28 sm:pt-36">
    <SEO title={title} description={description} canonical={`${window.location.origin}/legal/${page}`} robots="index, follow" />
    <article className="max-w-[760px] mx-auto px-5 sm:px-8 pb-28">
      <Eyebrow>RK Group — Legal</Eyebrow>
      <h1 className="font-serif font-light text-4xl sm:text-5xl tracking-tight text-[#FBF9F5] mt-5">{title}</h1>
      {subtitle && (
        <p className="font-serif italic text-lg sm:text-xl text-[#94A3B8] mt-4">{subtitle}</p>
      )}
      <p className="text-[11px] uppercase tracking-[0.25em] text-[#C5A059] mt-6 font-semibold" data-testid="legal-last-updated">
        Last Updated: {LAST_UPDATED}
      </p>
      <div className="w-16 h-px bg-[#C5A059]/50 mt-10" />
      <div className="mt-12 space-y-7">
        {children}
      </div>
      <nav className="mt-20 pt-8 border-t border-white/10 flex flex-wrap gap-x-8 gap-y-3" aria-label="Legal navigation">
        {[
          ["/legal/privacy-policy", "Privacy Policy"],
          ["/legal/terms-of-use", "Terms of Use"],
          ["/legal/cookie-policy", "Cookie Policy"],
          ["/legal/disclaimers", "Disclaimers"],
        ].map(([to, label]) => (
          <Link key={to} to={to} data-testid={`legal-nav-${to.split("/").pop()}`}
            className={`text-[11px] uppercase tracking-[0.18em] py-2 ${`/legal/${page}` === to ? "text-[#C5A059]" : "text-[#94A3B8] hover:text-[#FBF9F5]"} transition-colors`}>
            {label}
          </Link>
        ))}
      </nav>
    </article>
  </div>
);

const BodyText = ({ children }) => (
  <p className="text-sm sm:text-base text-[#FBF9F5]/65 leading-[1.9]">{children}</p>
);

function Disclaimers() {
  const location = useLocation();
  useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.slice(1));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
    }
  }, [location.hash]);

  return (
    <LegalShell
      title="Disclaimers"
      subtitle="Important Information Regarding Our Website, Jewellery and Real Estate Businesses"
      page="disclaimers"
      description="Disclaimers for the RK Group website — gold rates, jewellery pricing, real estate, RERA information and website governance."
    >
      <nav aria-label="Disclaimer index" className="border border-white/10 divide-y divide-white/10" data-testid="disclaimer-index">
        {DISCLAIMER_SECTIONS.map((s) => (
          <button
            key={s.id}
            onClick={() => document.getElementById(s.id)?.scrollIntoView({ behavior: "smooth", block: "start" })}
            data-testid={`disclaimer-index-${s.id}`}
            className="group w-full flex items-center gap-6 px-6 sm:px-8 py-5 text-left hover:bg-[#0B1320] transition-colors duration-300"
          >
            <span className="font-serif text-2xl text-[#C5A059]/40 group-hover:text-[#C5A059] transition-colors duration-300">{s.num}</span>
            <span className="font-serif text-xl sm:text-2xl text-[#FBF9F5] group-hover:text-[#C5A059] transition-colors duration-300">{s.title}</span>
          </button>
        ))}
      </nav>

      {DISCLAIMER_SECTIONS.map((s) => (
        <section key={s.id} id={s.id} className="scroll-mt-28 pt-16" data-testid={`disclaimer-${s.id}`}>
          <p className="font-serif text-2xl text-[#C5A059]/40">{s.num}</p>
          <h2 className="font-serif font-light text-2xl sm:text-3xl text-[#FBF9F5] mt-3">{s.title}</h2>
          <p className="text-[10px] uppercase tracking-[0.22em] text-[#C5A059]/80 mt-3 font-semibold">
            Last Updated: {LAST_UPDATED}
          </p>
          <div className="mt-7 space-y-6">
            {s.body.map((p, i) => (
              <BodyText key={i}>{p}</BodyText>
            ))}
          </div>
        </section>
      ))}
    </LegalShell>
  );
}

export default function Legal() {
  const { page } = useParams();

  if (RETIRED[page]) {
    return <Navigate to={`/legal/disclaimers#${RETIRED[page]}`} replace />;
  }

  if (page === "disclaimers") {
    return <Disclaimers />;
  }

  const doc = POLICY_PAGES[page];
  if (!doc) {
    return (
      <div className="pt-40 pb-32 text-center" data-testid="legal-not-found">
        <h1 className="font-serif text-4xl text-[#FBF9F5]">Page not found</h1>
        <Link to="/" className="text-[#C5A059] text-sm mt-4 inline-block">Back to home</Link>
      </div>
    );
  }

  return (
    <LegalShell title={doc.title} page={page} description={doc.description}>
      {doc.body.map((p, i) => (
        <BodyText key={i}>{p}</BodyText>
      ))}
    </LegalShell>
  );
}
